/**
 * StepApplication.jsx
 *
 * Tool Finder wizard step 1 (Explore path). Asks the user what they are
 * drilling for and stores the answer in `selections.application`.
 *
 * Does NOT auto-advance — the shared NavigationButtons component drives
 * Next/Back. handleSelect is the generic WizardContext mutator and only
 * writes to state; advancing the step is the Next button's job, which
 * calls handleNextExplore (which routes APPLICATION -> DRILLING_METHOD).
 *
 * Taxonomy source: imports `APPLICATIONS` from the shared
 * `@data/wizardData` module (ported verbatim from TH ToolFinder v4
 * `src/constants/index.js`). Ids / labels match TH TF v4 exactly so any
 * downstream filter keyed on `application_c` works against the same
 * vocabulary. Sublabels are an MRT-side UX addition kept locally as a
 * lookup map (TH TF v4 renders only the label). Icon strings are
 * text-chip placeholders pending real artwork.
 *
 * @module components/wizard/StepApplication
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3a — reverted to TH TF v4 ids same day)
 *                   (D-prep refactor: APPLICATIONS now imported from @data/wizardData)
 */

import { useWizard } from '@contexts/WizardContext';
import TileSelector from '@components/ui/TileSelector';
import { APPLICATIONS } from '@data/wizardData';

// MRT-side UX additions — TH TF v4's APPLICATIONS array has only id/label/icon.
// Sublabels add context to each tile; text-chip codes stand in for icon artwork.
const APPLICATION_SUBLABELS = {
  construction:  'Foundations, piling, anchoring',
  tunneling:     'Face drilling, rail / road',
  well_drilling: 'Water and geothermal wells',
  underground:   'Production / development',
  surface:       'Open-pit / bench drilling',
  quarrying:     'Aggregate and dimension stone',
};

const APPLICATION_ICON_CHIPS = {
  construction:  'CN',
  tunneling:     'TN',
  well_drilling: 'WD',
  underground:   'UG',
  surface:       'SF',
  quarrying:     'QR',
};

export default function StepApplication() {
  const { selections, handleSelect } = useWizard();

  const tiles = APPLICATIONS.map((app) => ({
    id: app.id,
    label: app.label,
    sublabel: APPLICATION_SUBLABELS[app.id] || '',
    icon: APPLICATION_ICON_CHIPS[app.id] || app.icon,
  }));

  return (
    <section aria-labelledby="application-heading" className="max-w-5xl mx-auto">
      <header className="mb-6">
        <h2
          id="application-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black"
        >
          What are you drilling for?
        </h2>
        <p className="mt-1 font-sandvik-text text-n6">
          Pick the closest application &mdash; you can refine on the next step.
        </p>
      </header>

      <TileSelector
        tiles={tiles}
        value={selections.application}
        onChange={(id) => handleSelect('application', id)}
        cols={{ mobile: 2, tablet: 3, desktop: 3 }}
        variant="icon"
        ariaLabel="Application"
      />
    </section>
  );
}
